import { Collection, Model } from 'web-mojo';

// Shared status→badge map for firmware update rows. Lives here so the device
// panel (DeviceFirmwareView) and the OTA Activity page (FirmwareUpdatesPage)
// render identical badges from one source instead of drifting copies. `queued`
// = waiting for the device to reconnect; `cancelled` = a queued intent retired
// before it fired (by the user, or by a reconnect that resolved to a skip).
const FIRMWARE_UPDATE_BADGES = 'badge:queued=warning,sent=info,started=info,downloading=primary,downloaded=primary,verifying=primary,applying=primary,success=success,failed=danger,rolled_back=warning,superseded=secondary,cancelled=dark';

class FirmwareUpdate extends Model {
    constructor(data = {}) {
        super(data, {
            endpoint: '/api/firmware/update'
        });
    }

    isTerminal() {
        // queued is non-terminal (still waiting for reconnect); cancelled is terminal.
        return ['success', 'failed', 'rolled_back', 'superseded', 'cancelled'].includes(this.get('status'));
    }
}

class FirmwareUpdateList extends Collection {
    constructor(options = {}) {
        super({
            ModelClass: FirmwareUpdate,
            endpoint: '/api/firmware/update',
            ...options
        });
    }
}

// Cancel a queued OTA update from either surface (device panel or OTA Activity).
// The server (POST /api/firmware/ota/cancel) is authoritative and races the
// reconnect dispatcher atomically: a cancel that lost the race comes back
// `not_cancellable` — still a successful, honest outcome the caller should
// refresh on, so both cancellable results return true and only a real failure
// returns false. `group` is scope-only (names the group a member-tier
// manage_firmware grant resolves in), exactly like the trigger endpoint; the
// update id is the target.
async function cancelQueuedUpdate(app, updateId, groupId) {
    const response = await app.rest.POST('/api/firmware/ota/cancel', {
        update: updateId,
        group: groupId,
    });
    if (!response.success || !response.data.status) {
        app.toast.error(response.data?.error || 'Failed to cancel the queued update');
        return false;
    }
    const result = response.data.data?.result;
    if (result === 'cancelled') {
        app.toast.success('Queued update cancelled');
        return true;
    }
    if (result === 'not_cancellable') {
        app.toast.info('Too late to cancel — the update already dispatched');
        return true;
    }
    app.toast.error(response.data?.error || 'Failed to cancel the queued update');
    return false;
}

export { FirmwareUpdate, FirmwareUpdateList, FIRMWARE_UPDATE_BADGES, cancelQueuedUpdate };
